// app/components/thailand-premium-tapioca-starch-products/TapiocaChips.tsx
"use client";

import * as React from "react";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import { Box, Grid } from "@mui/material";
import { useLocale, useTranslations } from "next-intl";
import Image from "next/image";

type InlineEdit = {
  id: string;
  type: "text" | "image";
  selector: string;
  value: string;
  cmsKey?: string;
};

export default function TapiocaChips() {
  const t = useTranslations("OurProducts");
  const locale = useLocale();

  const pagePath = "/thailand-premium-tapioca-starch-products"; 

  // ---- default จาก i18n (SSR / SEO) ----
  const defaultTitle = t("Chips.title");
  const defaultContent = t("Chips.content");
  const defaultSubtitle = t("Chips.subtitle");
  const defaultImage = "/assets/thailand-premium-tapioca-starch-products/chips/img1.jpg";
  // ---- state ที่ใช้แสดงจริง (จะถูก override ด้วย inline edits ถ้ามี) ----
  const [title, setTitle] = React.useState(defaultTitle);
  const [content, setContent] = React.useState(defaultContent);
  const [subtitle, setSubtitle] = React.useState(defaultSubtitle);
  const [imageSrc, setImageSrc] = React.useState(defaultImage);

  // เมื่อ locale เปลี่ยน → reset เป็นค่า default ก่อน
  React.useEffect(() => {
    setTitle(defaultTitle);
    setContent(defaultContent);
    setSubtitle(defaultSubtitle);
    setImageSrc(defaultImage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale]);

 //
  React.useEffect(() => {
    const fetchInline = async () => {
      try {
        const res = await fetch(
          `/api/admin/page-inline?websiteId=${process.env.NEXT_PUBLIC_WEBSITE_ID || process.env.WEBSITE_ID}&path=${encodeURIComponent(
            pagePath
          )}&locale=${locale}`,
          {
            credentials: "include",
            cache: "no-store",
          }
        );

        if (!res.ok) return;

        const data = await res.json();
        if (!data?.edits) return;

        const list: InlineEdit[] = data.edits;

        // สร้าง map จาก cmsKey → value
        const map: Record<string, string> = {};

        list.forEach((e) => {
          const keyFromCms = e.cmsKey;
          if (keyFromCms) {
            map[keyFromCms] = e.value;
            return;
          }

          // fallback: เผื่อ row เก่า ๆ ที่ยังเก็บ key อยู่ใน selector
          const match = e.selector?.match(/data-cms-key="([^"]+)"/);
          if (match) {
            const key = match[1];
            map[key] = e.value;
          }
        });

        // override เฉพาะ key ที่มีใน inline
        if (map["ourproducts.chips.title"]) {
          setTitle(map["ourproducts.chips.title"]);
        }
        if (map["ourproducts.chips.content"]) {
          setContent(map["ourproducts.chips.content"]);
        }
        if (map["ourproducts.chips.subtitle"]) {
          setSubtitle(map["ourproducts.chips.subtitle"]);
        }
        if (map["ourproducts.chips.image"]) {
          setImageSrc(map["ourproducts.chips.image"]);
        }
      } catch (err) {
        console.warn("[TapiocaChips] inline fetch error:", err);
      }
    };

    fetchInline();
  }, [locale, pagePath]);

  return (
    <Box
      id="tapioca-chips"
      className="chips-wrap"
      sx={{
        pt: { xs: 0, md: 2 },
        pb: { xs: 0, md: 2 }
      }}>
      <Container className="container">
        <Grid
          container
          spacing={{ xs: 3, md: 10 }}
          sx={{
            justifyContent: "center",
            alignItems: "center",
            py: 3
          }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography
              variant="h2"
              data-aos="fade-up"
              data-cms-key="ourproducts.chips.title"
              sx={{
                mb: 3,
                whiteSpace: { xs: "normal", lg: "pre-line" }
              }}>
              {title}
            </Typography>

            <Typography
              variant="body1"
              data-aos="fade-up"
              data-cms-key="ourproducts.chips.content"
              sx={{
                mb: 3
              }}
            >
              {content}
            </Typography>

            <Typography
              variant="h5"
              data-aos="fade-up"
              data-cms-key="ourproducts.chips.subtitle"
            >
              {subtitle}
            </Typography>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }} data-aos="fade-left">
            <Image
              src={imageSrc}
              title="thailand tapioca chips"
              alt="Tapioca chips from Thailand for alcohol production and animal feed"
              width={608}
              height={354}
              className="img-fluid chips-image"
              data-cms-key="ourproducts.chips.image"
            />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}